/**
 * Gateway connection context & hook.
 *
 * Provides a single GatewayClient instance shared across the component tree.
 * Auth resolution order (post-2026-04-30 auth-flow pivot):
 *   1. Direct token override (instance-token impersonation flow).
 *   2. Cached Lexiun JWT from `lib/lexiun-jwt.ts` (Clerk-exchange or
 *      dev-login seed). Same JWT PlatformApi uses — single exchange.
 *   3. localStorage key "lexportal.gateway.token" → simple shared token.
 *   4. Dev fallback: well-known "lexdev" gateway token.
 *
 * Pre-pivot, this provider had its own `exchangeClerkToken` that hit
 * `/portal/api/auth/exchange` on lexruntime. Both responsibilities now
 * live in `getLexiunToken` (shared helper); this provider just consumes
 * the cached value.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { GatewayClient, type GatewayEventFrame } from "@/lib/gateway-client";
import { getLexiunToken } from "@/lib/lexiun-jwt";
import { clientLog } from "@/lib/client-log";
import type { GatewayConnectionState } from "@/types/metrics";

const LS_TOKEN_KEY = "lexportal.gateway.token";

// ── Context ─────────────────────────────────────────────────────

type GatewayContextValue = {
  state: GatewayConnectionState;
  rpc: <T = unknown>(method: string, params?: unknown) => Promise<T>;
  subscribe: (handler: (evt: GatewayEventFrame) => void) => () => void;
};

const GatewayContext = createContext<GatewayContextValue | null>(null);

// ── Provider ────────────────────────────────────────────────────

type GatewayProviderProps = {
  wsUrl?: string;
  /** Direct token override — used when connecting to a remote runtime via instance token */
  token?: string;
  /**
   * Pre-pivot LexPortal accepted a Clerk-token getter and exchanged it
   * inline. Post-2026-04-30 the exchange happens inside `getLexiunToken`
   * (driven by the module-level Clerk-token resolver registered by
   * `<ClerkTokenBridge>` in App.tsx). Prop preserved for callers that
   * still pass it but no longer consulted.
   */
  getClerkToken?: () => Promise<string | null>;
  children: ReactNode;
};

export function GatewayProvider({ wsUrl, token: tokenOverride, children }: GatewayProviderProps) {
  const [state, setState] = useState<GatewayConnectionState>("disconnected");
  const clientRef = useRef<GatewayClient | null>(null);
  const eventHandlersRef = useRef<Set<(evt: GatewayEventFrame) => void>>(new Set());

  useEffect(() => {
    let cancelled = false;
    // Hoisted at useEffect scope so both `init()` and the
    // visibility-change handler reuse one logger instance instead of
    // re-resolving it on every wake-from-hidden event.
    const log = clientLog.forSubsystem("gateway");

    async function init() {
      // Production-mode detection: when VITE_LEXSERVER_WS_URL is set,
      // the SPA opens a direct cross-origin WS to lexserver's runtime
      // proxy and the in-band `connect` frame must carry the per-org
      // gateway token (not the dev `lexdev` fallback or the Lexiun JWT).
      // See ai-docs/per-user-runtime-proxy-plan.md (P2.B).
      const productionWsUrl = (import.meta.env.VITE_LEXSERVER_WS_URL as string | undefined)?.trim();
      const productionMode = Boolean(productionWsUrl) && !tokenOverride;

      // Lexiun JWT is needed for both the WS subprotocol routing token
      // and the agent/connect call that returns the gateway token.
      const lexiunJwt = await getLexiunToken();

      // Resolve in-band gateway token
      let token: string | undefined;
      let subprotocols: string[] | undefined;

      if (tokenOverride) {
        token = tokenOverride;
      } else if (productionMode && lexiunJwt) {
        // Fetch the per-org gateway token for the user's runtime so the
        // in-band `connect` frame authenticates against the production
        // lexruntime gateway. Lexiun JWT rides along in the subprotocol
        // for the proxy's upgrade-time routing decision.
        try {
          const res = await fetch("/api/agent/connect", {
            headers: { Authorization: `Bearer ${lexiunJwt}` },
          });
          if (res.ok) {
            const body = (await res.json()) as {
              data?: { gatewayToken?: string; gatewayTokenError?: string };
            };
            if (body.data?.gatewayToken) {
              token = body.data.gatewayToken;
            } else {
              console.warn(
                "[gateway] agent/connect returned no gatewayToken:",
                body.data?.gatewayTokenError ?? "(no error message)",
              );
            }
          } else {
            console.warn("[gateway] agent/connect failed:", res.status);
          }
        } catch (err) {
          console.warn("[gateway] agent/connect threw:", err);
        }
        subprotocols = [`lexiun-jwt.${lexiunJwt}`];
      } else {
        // Dev-mode token resolution (unchanged from pre-P2.B).
        if (lexiunJwt) {
          token = lexiunJwt;
        } else {
          const stored = localStorage.getItem(LS_TOKEN_KEY);
          if (stored) token = stored;
          else if (import.meta.env.DEV) token = "lexdev";
        }
      }

      if (cancelled) return;

      // Resolve WS URL — production override > caller > location-derived dev path.
      const url =
        wsUrl ??
        productionWsUrl ??
        (() => {
          const proto = location.protocol === "https:" ? "wss:" : "ws:";
          // Use /gateway-ws in dev to avoid Vite HMR WS path conflict
          const wsPath = import.meta.env.DEV ? "/gateway-ws" : "/ws";
          return `${proto}//${location.host}${wsPath}`;
        })();

      // Re-fetch auth on every reconnect so a tab left open past the
      // Lexiun JWT TTL (~1h) recovers without a manual page reload.
      // Mirrors lexide/src/hooks/useGateway.ts. Dev-mode + tokenOverride
      // skip the refresh hook because the cred isn't a short-lived JWT.
      const refreshAuth = (productionMode && !tokenOverride)
        ? async () => {
            const freshJwt = await getLexiunToken();
            if (!freshJwt) {
              // getLexiunToken returns null when both Clerk session AND
              // the localStorage dev seed are absent — typically because
              // the user signed out in another tab. Stop the reconnect
              // loop so the operational-log isn't spammed for the ~30s
              // window before Clerk's <SignedOut>+<RedirectToSignIn>
              // unmounts the gateway provider. Mirrors lexide.
              log.warn("gateway.auth.session-lost", { reason: "getLexiunToken returned null" });
              clientRef.current?.stop();
              throw new Error("getLexiunToken returned null");
            }
            const res = await fetch("/api/agent/connect", {
              headers: { Authorization: `Bearer ${freshJwt}` },
            });
            if (!res.ok) throw new Error(`agent/connect ${res.status}`);
            const body = (await res.json()) as { data?: { gatewayToken?: string } };
            const newGatewayToken = body.data?.gatewayToken;
            if (!newGatewayToken) throw new Error("agent/connect did not return gatewayToken");
            log.info("gateway.auth.refreshed", { jwt_len: freshJwt.length });
            return {
              token: newGatewayToken,
              subprotocols: [`lexiun-jwt.${freshJwt}`],
            };
          }
        : undefined;
      const client = new GatewayClient({
        url,
        token,
        subprotocols,
        refreshAuth,
        onStateChange: (s) => {
          if (!cancelled) setState(s);
          // Mirror connection lifecycle to the operational log so
          // platform admins see lexportal's gateway state per-session.
          // GatewayConnectionState in lexportal: disconnected/connecting/connected/error.
          const isProblem = s === "error" || s === "disconnected";
          const fields = { state: s, gateway_url: url, production_mode: productionMode };
          if (isProblem) log.warn(`gateway.state.${s}`, fields);
          else log.info(`gateway.state.${s}`, fields);
        },
        onEvent: (evt) => {
          for (const handler of eventHandlersRef.current) {
            try {
              handler(evt);
            } catch (err) {
              console.error("[gateway] event handler error:", err);
              log.error("gateway.event_handler_error", err instanceof Error ? err : new Error(String(err)));
            }
          }
        },
      });

      clientRef.current = client;
      client.start();
    }

    void init();

    // Visibility-change reconnect: when the tab returns from hidden
    // (laptop wake / overnight idle), force an immediate fresh-auth
    // reconnect instead of waiting up to the 30s backoff ceiling.
    // Mirrors lexide/src/hooks/useGateway.ts. Runs in BOTH dev and
    // production — in dev `refreshAuth` is undefined so the client
    // reconnects with cached creds (same as the regular backoff
    // loop would do).
    const VISIBILITY_WAKE_THRESHOLD_MS = 30_000;
    let lastHiddenAt: number | null = null;
    const onVisibilityChange = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "hidden") {
        lastHiddenAt = Date.now();
      } else if (document.visibilityState === "visible" && lastHiddenAt !== null) {
        const hiddenForMs = Date.now() - lastHiddenAt;
        lastHiddenAt = null;
        if (hiddenForMs > VISIBILITY_WAKE_THRESHOLD_MS) {
          log.info("gateway.visibility.wake.force-reconnect", { hidden_for_ms: hiddenForMs });
          clientRef.current?.forceReconnect();
        }
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      cancelled = true;
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
      clientRef.current?.stop();
      clientRef.current = null;
    };
  }, [wsUrl, tokenOverride]);

  const rpc = useCallback(<T = unknown>(method: string, params?: unknown): Promise<T> => {
    if (!clientRef.current) return Promise.reject(new Error("gateway not initialized"));
    return clientRef.current.request<T>(method, params);
  }, []);

  const subscribe = useCallback((handler: (evt: GatewayEventFrame) => void) => {
    eventHandlersRef.current.add(handler);
    return () => {
      eventHandlersRef.current.delete(handler);
    };
  }, []);

  return (
    <GatewayContext.Provider value={{ state, rpc, subscribe }}>
      {children}
    </GatewayContext.Provider>
  );
}

// ── Hook ────────────────────────────────────────────────────────

export function useGatewayRpc(): GatewayContextValue {
  const ctx = useContext(GatewayContext);
  if (!ctx) throw new Error("useGatewayRpc must be used within a GatewayProvider");
  return ctx;
}
